// agora.js
'use strict';

import loadScriptTag from '../resource/loadScriptTag.js';
import env from '../resource/env.js';
import { player as playerDebug } from '../resource/debug.js';

const log = playerDebug.extend('log:agora');
const errorLog = playerDebug.extend('error:agora');

let agoraClient;
// Agora SDK
let loadAgoraScriptPromise;
let currentAgoraInstance = null;
let pendingLeavePromise = null;

export const loadAgoraScript = async () => {
  if (loadAgoraScriptPromise) {
    await loadAgoraScriptPromise;
    return;
  }

  loadAgoraScriptPromise = loadScriptTag({
    id: `agora-rtc-${env.AGORA_RTC_VERSION}`,
    src: `https://download.agora.io/sdk/release/AgoraRTC_N-${env.AGORA_RTC_VERSION}.js`,
  });

  log('start load agora client');
  await loadAgoraScriptPromise;
  log('load agora client done');

  if (window.AgoraRTC) {
    // AgoraRTC default enable debug log
    // 0: DEBUG. Output all API logs.
    // 1: INFO. Output logs of the INFO, WARNING and ERROR level.
    // 2: WARNING. Output logs of the WARNING and ERROR level.
    // 3: ERROR. Output logs of the ERROR level.
    // 4: NONE. Do not output any log.
    let debugLevel = 4;
    if (new URLSearchParams(window.location.search).has('debug-agora')) {
      debugLevel = +new URLSearchParams(window.location.search).get(
        'debug-agora'
      );
    }
    window.AgoraRTC.setLogLevel(debugLevel);
  }
};

export const initializeAgoraClient = ({ codec }) => {
  log('execute initialize agora client', { codec });
  // create client reference: https://api-ref.agora.io/en/video-sdk/web/4.x/interfaces/clientconfig.html
  return window.AgoraRTC.createClient({
    mode: 'live',
    role: 'audience',
    clientRoleOptions: {
      // reference: https://doc.shengwang.cn/doc/rtc/javascript/basic-features/ls-quick-start
      // reference: https://api-ref.agora.io/en/video-sdk/web/4.x/enums/audiencelatencyleveltype.html
      // 1: Low latency.
      // 2: (Default) Ultra low latency.
      level: 1,
    },
    codec,
  });
};

// https://api-ref.agora.io/en/video-sdk/web/4.x/globals.html#connectionstate
export const AGORA_CLIENT_CONNECTION_STATE = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  DISCONNECTING: 'DISCONNECTING',
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/enums/remotestreamtype
export const REMOTE_STREAM_TYPE = {
  HIGH: 0,
  LOW: 1,
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/enums/remotestreamfallbacktype
export const REMOTE_STREAM_FALLBACK_TYPE = {
  DISABLE: 0,
  VIDEO_STREAM_LOW: 1,
  AUDIO_ONLY: 2,
};

export const getAgoraConnectionState = ({ channelId }) => {
  return getAgoraInstance({ channelId })?.client?.connectionState;
};

export const bindAgoraRTCEventListeners = ({ listeners }) => {
  listeners.forEach(({ eventName, listener }) => {
    window.AgoraRTC?.on(eventName, listener);
  });
};

export const unbindAgoraRTCEventListeners = ({ listeners }) => {
  listeners.forEach(({ eventName, listener }) => {
    window.AgoraRTC?.off(eventName, listener);
  });
};

export const cleanupAgoraRTCListeners = () => {
  window.AgoraRTC?.removeAllListeners();
};

// Agora Instance Operations (listeners bind/unbind & renew token & leave)
export const bindAgoraClientEventListeners = ({ channelId, listeners }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  listeners.forEach(({ eventName, listener }) => {
    agoraClient?.on(eventName, listener);
  });
};

export const unbindAgoraClientEventListeners = ({ channelId, listeners }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  listeners.forEach(({ eventName, listener }) => {
    agoraClient?.off(eventName, listener);
  });
};

export const renewToken = async ({ channelId, token }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  if (!agoraClient) {
    errorLog('fail to renew token: agora client not found', {
      channelId,
      token,
    });
    return;
  }
  try {
    await agoraClient.renewToken(token);
    log('successfully renewed token', { channelId, token });
  } catch (error) {
    errorLog('fail to renew token', { channelId, token, error });
    throw error;
  }
};

/**
 * Agora Client Promise Chain Flow
 *
 * joinChannelWithoutSubscribingPromise (Preloader 觸發，Player 複用)
 * │
 * ├─ 1. loadScriptPromise           ← loadAgoraScript()
 * │
 * ├─ 2. createClientPromise          ← initializeAgoraClient()
 * │     └─ bindInternalClientListeners()
 * │
 * ├─ 3. joinChannelPromise           ← client.join(appId, channelId, token, sessionId)
 * │
 * └─ 4. waitForPublishingPromise     ← wait video + audio user-published (10s timeout)
 *       └─ resolve()
 *
 *
 * joinChannelWithSubscribingPromise (Player 觸發)
 * │
 * ├─ reuse joinChannelWithoutSubscribingPromise
 * │  (若 Preloader 已建立 → 直接 await，不重建)
 * │
 * └─ 5. subscribeTrackPromise        ← client.subscribe(remoteUser, 'video')
 *                                       client.subscribe(remoteUser, 'audio')
 *       └─ return { client, track: { videoTrack, audioTrack } }
 *
 *
 * AgoraInstance Property Reset Levels
 * (僅重置 agoraInstance 上的 cached promise，不會觸發 SDK 的 join/leave/subscribe)
 *
 * resetSubscription ⊂ resetPublishing ⊂ resetConnection
 *
 * resetSubscription: subscribeTrackPromise, joinChannelWithSubscribingPromise
 * resetPublishing:   + waitForPublishingPromise, joinChannelWithoutSubscribingPromise, publishedResolve
 * resetConnection:   + joinChannelPromise, publishedTracks
 */

const WAIT_FOR_PUBLISHING_TIMEOUT_MS = 10000;
export const AGORA_ERROR_WAIT_FOR_PUBLISHING_TIMEOUT =
  'agora_player_error_9999_publishing_timeout';

// --- AgoraInstance Property Reset Helpers (no SDK operations) ---

const getIsCurrentAgoraInstance = agoraInstance => {
  return (
    currentAgoraInstance === agoraInstance &&
    !agoraInstance.abortController.signal.aborted
  );
};

// Abort async flows in agoraClient join / subscribe processes
const waitWithAbort = (promise, agoraInstance) => {
  const { signal } = agoraInstance.abortController;
  if (signal.aborted) return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const handleAbort = () => resolve(null);
    signal.addEventListener('abort', handleAbort, { once: true });
    Promise.resolve(promise).then(
      value => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      error => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

const abortAgoraInstance = agoraInstance => {
  if (!agoraInstance.abortController.signal.aborted) {
    agoraInstance.abortController.abort();
  }
};

const resetSubscription = agoraInstance => {
  agoraInstance.subscribeTrackPromise = null;
  agoraInstance.joinChannelWithSubscribingPromise = null;
};

const resetPublishing = agoraInstance => {
  resetSubscription(agoraInstance);
  agoraInstance.waitForPublishingPromise = null;
  agoraInstance.joinChannelWithoutSubscribingPromise = null;
  agoraInstance.publishedResolve = null;
  clearTimeout(agoraInstance.publishingTimeoutId);
  agoraInstance.publishingTimeoutId = null;
};

const resetConnection = agoraInstance => {
  resetPublishing(agoraInstance);
  agoraInstance.joinChannelPromise = null;
  agoraInstance.publishedTracks = {};
};

const createAgoraEntry = channelId => ({
  channelId,
  abortController: new AbortController(),
  client: null,
  loadScriptPromise: null,
  createClientPromise: null,
  joinChannelPromise: null,
  waitForPublishingPromise: null,
  subscribeTrackPromise: null,
  joinChannelWithoutSubscribingPromise: null,
  joinChannelWithSubscribingPromise: null,
  publishedResolve: null,
  publishingTimeoutId: null,
  publishedTracks: {},
  isNearBy: false,
});

export const getAgoraInstance = ({ channelId } = {}) => {
  if (!currentAgoraInstance) return null;
  if (channelId && currentAgoraInstance.channelId !== channelId) return null;
  return currentAgoraInstance;
};

export const acquireAgoraInstance = async ({ channelId } = {}) => {
  if (!channelId) {
    throw new Error('acquireAgoraInstance: missing channelId');
  }

  if (pendingLeavePromise) {
    await pendingLeavePromise;
  }

  if (currentAgoraInstance?.channelId === channelId) {
    return currentAgoraInstance;
  }

  if (currentAgoraInstance) {
    await leaveAgoraChannel({ channelId: currentAgoraInstance.channelId });
  }

  currentAgoraInstance = createAgoraEntry(channelId);
  return currentAgoraInstance;
};

export const leaveAgoraChannel = async ({ channelId } = {}) => {
  if (pendingLeavePromise) {
    await pendingLeavePromise;
    return;
  }

  const agoraInstance = getAgoraInstance({ channelId });
  if (!agoraInstance) return;

  pendingLeavePromise = (async () => {
    abortAgoraInstance(agoraInstance);
    resetConnection(agoraInstance);
    const client = agoraInstance.client || agoraClient;
    if (!client) return;

    client.removeAllListeners();
    await client.leave().catch(error => {
      errorLog('leaveAgoraChannel: fail to leave channel', {
        channelId: agoraInstance.channelId,
        error,
      });
    });
  })();

  try {
    await pendingLeavePromise;
  } finally {
    if (currentAgoraInstance === agoraInstance) {
      currentAgoraInstance = null;
    }
    pendingLeavePromise = null;
  }
};

// --- Internal Helpers ---

const collectTracksFromRemoteUsers = client => {
  let uid = null;
  let videoTrack = null;
  let audioTrack = null;
  for (const remoteUser of client?.remoteUsers || []) {
    if (remoteUser?.videoTrack) {
      uid = remoteUser.uid;
      videoTrack = remoteUser.videoTrack;
    }
    if (remoteUser?.audioTrack) {
      uid = remoteUser.uid;
      audioTrack = remoteUser.audioTrack;
    }
  }
  return { uid, videoTrack, audioTrack };
};

const bindInternalClientListeners = (client, agoraInstance, channelId) => {
  client.on('user-published', (remoteUser, mediaType) => {
    agoraInstance.publishedTracks[mediaType] = remoteUser;
    log('user-published', { channelId, mediaType, uid: remoteUser.uid });
    if (
      agoraInstance.publishedTracks.video &&
      agoraInstance.publishedTracks.audio &&
      agoraInstance.publishedResolve
    ) {
      // Cancel the timeout race so it won't reject after resolve.
      clearTimeout(agoraInstance.publishingTimeoutId);
      agoraInstance.publishingTimeoutId = null;
      agoraInstance.publishedResolve();
    }
  });

  client.on('user-unpublished', (remoteUser, mediaType) => {
    log('user-unpublished', { channelId, mediaType });
    delete agoraInstance.publishedTracks[mediaType];
    resetPublishing(agoraInstance);
  });

  client.on('connection-state-change', (curState, prevState, reason) => {
    log('connection-state-change', { channelId, curState, prevState, reason });
    if (curState === 'DISCONNECTED') {
      resetConnection(agoraInstance);
    }
  });
};

// --- Remote Stream Selection & Fallback Helpers ---

// https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/iagorartcclient#setRemoteDefaultVideoStreamType
const setAgoraRemoteDefaultVideoStreamType = async ({ client, channelId }) => {
  const defaultStreamType = REMOTE_STREAM_TYPE.LOW;
  try {
    await client.setRemoteDefaultVideoStreamType(defaultStreamType);
    log('setRemoteDefaultVideoStreamType: success', {
      channelId,
      defaultStreamType,
    });
  } catch (error) {
    errorLog('setRemoteDefaultVideoStreamType: failed', {
      channelId,
      defaultStreamType,
      error,
    });
  }
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/iagorartcclient#setStreamFallbackOption
const setAgoraStreamFallbackOption = async ({
  client,
  channelId,
  uid,
  fallbackType = REMOTE_STREAM_FALLBACK_TYPE.VIDEO_STREAM_LOW,
}) => {
  if (uid == null) return false;

  try {
    await client.setStreamFallbackOption(uid, fallbackType);
    log('setStreamFallbackOption: success', {
      channelId,
      uid,
      fallbackType,
    });
    return true;
  } catch (error) {
    errorLog('setStreamFallbackOption: failed', {
      channelId,
      uid,
      fallbackType,
      error,
    });
    return false;
  }
};

// --- Core Flows ---

const joinChannelWithoutSubscribingCore = async ({
  appId,
  channelId,
  token,
  sessionId,
  agoraInstance,
}) => {
  // load script
  if (!agoraInstance.loadScriptPromise) {
    agoraInstance.loadScriptPromise = loadAgoraScript();
  }
  await waitWithAbort(agoraInstance.loadScriptPromise, agoraInstance);
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;

  // create client & bind internal listeners
  if (!agoraInstance.createClientPromise) {
    if (!agoraClient) {
      agoraClient = initializeAgoraClient({ codec: 'h264' });
    }
    agoraInstance.client = agoraClient;
    agoraInstance.createClientPromise = Promise.resolve(agoraClient);
    bindInternalClientListeners(agoraClient, agoraInstance, channelId);
  }
  const client = await waitWithAbort(
    agoraInstance.createClientPromise,
    agoraInstance
  );
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;

  // join channel — channelId is passed as the Agora SDK "channel" parameter
  if (!agoraInstance.joinChannelPromise) {
    if (!appId || !token) {
      throw new Error(
        `joinChannel failed: missing required params (appId: ${!!appId}, token: ${!!token})`
      );
    }
    // setRemoteDefaultVideoStreamType sets the initial subscribed stream type before join.
    await waitWithAbort(
      setAgoraRemoteDefaultVideoStreamType({ client, channelId }),
      agoraInstance
    );
    if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
    log('joinChannelWithoutSubscribing: joining', { channelId });
    agoraInstance.joinChannelPromise = client.join(
      appId,
      channelId,
      token,
      sessionId,
      // Ref: https://doc.shengwang.cn/doc/rtc/javascript/best-practice/preload#22-%E8%BF%9B%E5%85%A5%E9%A2%91%E9%81%93
      // Enable autoSubscribe only in preload flow.
      // We reuse a single active Agora client here, so only current livestream trigger autoSubscribe, thus agora subscribe cost stays limited.
      // In the multi-client (multi agoraClient, pool size > 1) early-join flow, enabling autoSubscribe would subscribe too early
      // and significantly increase Agora cost.
      { autoSubscribe: true }
    );
  }
  try {
    await waitWithAbort(agoraInstance.joinChannelPromise, agoraInstance);
  } catch (error) {
    errorLog('joinChannelWithoutSubscribingCore: join failed', {
      channelId,
      error,
    });
    resetConnection(agoraInstance);
    throw error;
  }
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
  log('joinChannelWithoutSubscribing: joined', { channelId });

  // wait for both video & audio published (with timeout)
  if (!agoraInstance.waitForPublishingPromise) {
    const { promise, resolve } = Promise.withResolvers();
    agoraInstance.publishedResolve = resolve;
    agoraInstance.waitForPublishingPromise = Promise.race([
      promise,
      new Promise((_, reject) => {
        agoraInstance.publishingTimeoutId = setTimeout(() => {
          agoraInstance.publishingTimeoutId = null;
          reject(new Error(AGORA_ERROR_WAIT_FOR_PUBLISHING_TIMEOUT));
          // Clear cached rejected promise so the player can retry fresh
          // instead of immediately re-hitting the already-rejected promise.
          resetPublishing(agoraInstance);
        }, WAIT_FOR_PUBLISHING_TIMEOUT_MS);
      }),
    ]);
    if (
      agoraInstance.publishedTracks.video &&
      agoraInstance.publishedTracks.audio
    ) {
      clearTimeout(agoraInstance.publishingTimeoutId);
      agoraInstance.publishingTimeoutId = null;
      resolve();
    }
  }

  return { client };
};

const joinChannelWithSubscribingCore = async ({
  appId,
  channelId,
  token,
  sessionId,
  agoraInstance,
}) => {
  if (!agoraInstance.joinChannelWithoutSubscribingPromise) {
    agoraInstance.joinChannelWithoutSubscribingPromise =
      joinChannelWithoutSubscribingCore({
        appId,
        channelId,
        token,
        sessionId,
        agoraInstance,
      });
  }
  const joinedChannelResult = await waitWithAbort(
    agoraInstance.joinChannelWithoutSubscribingPromise,
    agoraInstance
  );
  if (!joinedChannelResult || !getIsCurrentAgoraInstance(agoraInstance)) {
    return null;
  }
  const { client } = joinedChannelResult;
  log('joinChannelWithSubscribing: joined', { channelId });

  await waitWithAbort(agoraInstance.waitForPublishingPromise, agoraInstance);
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
  log('joinChannelWithSubscribing: published', { channelId });

  // subscribe & collect media tracks
  if (!agoraInstance.subscribeTrackPromise) {
    agoraInstance.subscribeTrackPromise = (async () => {
      const existingTracks = collectTracksFromRemoteUsers(client);
      if (existingTracks.videoTrack && existingTracks.audioTrack) {
        // setStreamFallbackOption lets Agora automatically downgrade video on weak networks.
        await waitWithAbort(
          setAgoraStreamFallbackOption({
            client,
            channelId,
            uid: existingTracks.uid,
          }),
          agoraInstance
        );
        if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
        log('joinChannelWithSubscribing: already subscribed', {
          channelId,
          ...existingTracks,
        });
        return existingTracks;
      }
      try {
        log('joinChannelWithSubscribing: subscribing', { channelId });
        for (const [mediaType, remoteUser] of Object.entries(
          agoraInstance.publishedTracks
        )) {
          if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
          await waitWithAbort(
            client.subscribe(remoteUser, mediaType),
            agoraInstance
          );
          if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
          log('joinChannelWithSubscribing: subscribed', {
            channelId,
            mediaType,
          });
          if (mediaType === 'video') {
            await waitWithAbort(
              setAgoraStreamFallbackOption({
                client,
                channelId,
                uid: remoteUser?.uid,
              }),
              agoraInstance
            );
            if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
          }
        }
      } catch (error) {
        errorLog('joinChannelWithSubscribing: subscribe failed', {
          channelId,
          error,
        });
        agoraInstance.subscribeTrackPromise = null;
        throw error;
      }
      return collectTracksFromRemoteUsers(client);
    })();
  }
  const track = await waitWithAbort(
    agoraInstance.subscribeTrackPromise,
    agoraInstance
  );
  if (!track || !getIsCurrentAgoraInstance(agoraInstance)) return null;
  return { client, track };
};

// --- Exported API ---

export const joinChannelWithoutSubscribing = async ({
  appId,
  channelId,
  token,
  sessionId,
}) => {
  const agoraInstance = await acquireAgoraInstance({ channelId });
  if (!agoraInstance.joinChannelWithoutSubscribingPromise) {
    agoraInstance.joinChannelWithoutSubscribingPromise =
      joinChannelWithoutSubscribingCore({
        appId,
        channelId,
        token,
        sessionId,
        agoraInstance,
      });
  }
  try {
    const joinedChannelResult = await waitWithAbort(
      agoraInstance.joinChannelWithoutSubscribingPromise,
      agoraInstance
    );
    if (!joinedChannelResult) return null;
    const { client } = joinedChannelResult;
    return client;
  } catch (error) {
    errorLog('joinChannelWithoutSubscribing: failed', { channelId, error });
    resetPublishing(agoraInstance);
    leaveAgoraChannel({ channelId });
    throw error;
  }
};

export const joinChannelWithSubscribing = async ({
  appId,
  channelId,
  token,
  sessionId,
}) => {
  const agoraInstance = await acquireAgoraInstance({ channelId });
  if (!agoraInstance.joinChannelWithSubscribingPromise) {
    agoraInstance.joinChannelWithSubscribingPromise =
      joinChannelWithSubscribingCore({
        appId,
        channelId,
        token,
        sessionId,
        agoraInstance,
      });
  }
  try {
    const subscribedChannelResult = await waitWithAbort(
      agoraInstance.joinChannelWithSubscribingPromise,
      agoraInstance
    );
    if (!subscribedChannelResult) return null;
    const { client, track } = subscribedChannelResult;
    return { client, track };
  } catch (error) {
    errorLog('joinChannelWithSubscribing: failed', { channelId, error });
    resetConnection(agoraInstance);
    leaveAgoraChannel({ channelId });
    throw error;
  }
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/iagorartcclient#setRemoteVideoStreamType
export const setAgoraRemoteVideoStreamType = async ({
  channelId,
  uid,
  streamType,
  shouldDisableFallback = false,
}) => {
  const client = getAgoraInstance({ channelId })?.client;

  if (!client) {
    errorLog('setRemoteVideoStreamType: agora client not found', {
      channelId,
      uid,
      streamType,
      shouldDisableFallback,
    });
    return false;
  }

  try {
    if (shouldDisableFallback) {
      await setAgoraStreamFallbackOption({
        client,
        channelId,
        uid,
        fallbackType: REMOTE_STREAM_FALLBACK_TYPE.DISABLE,
      });
    }

    await client.setRemoteVideoStreamType(uid, streamType);
    log('setRemoteVideoStreamType: success', {
      channelId,
      uid,
      streamType,
      shouldDisableFallback,
    });
    return true;
  } catch (error) {
    errorLog('setRemoteVideoStreamType: failed', {
      channelId,
      uid,
      streamType,
      shouldDisableFallback,
      error,
    });
    throw error;
  }
};

export const setAgoraRemoteStreamFallbackOption = async ({
  channelId,
  uid,
  fallbackType,
}) => {
  const client = getAgoraInstance({ channelId })?.client;

  if (!client) {
    errorLog('setStreamFallbackOption: agora client not found', {
      channelId,
      uid,
      fallbackType,
    });
    return false;
  }

  return setAgoraStreamFallbackOption({
    client,
    channelId,
    uid,
    fallbackType,
  });
};

export const cleanupAllAgoraInstances = async () => {
  if (!currentAgoraInstance) return;
  await leaveAgoraChannel({ channelId: currentAgoraInstance.channelId });
};

export const unsubscribeJoinedChannel = async ({ channelId }) => {
  const agoraInstance = getAgoraInstance({ channelId });
  if (!agoraInstance || !agoraInstance.client) {
    errorLog('unsubscribeJoinedChannel: agora client not found', { channelId });
    return;
  }
  log('unsubscribeJoinedChannel: start', { channelId });
  resetSubscription(agoraInstance);
  const remoteUsers = agoraInstance.client?.remoteUsers;
  if (!remoteUsers?.length) {
    errorLog('unsubscribeJoinedChannel: no remote users', { channelId });
    return;
  }
  try {
    for (const remoteUser of remoteUsers) {
      if (remoteUser?.videoTrack) {
        await agoraInstance.client.unsubscribe(remoteUser, 'video');
      }
      if (remoteUser?.audioTrack) {
        await agoraInstance.client.unsubscribe(remoteUser, 'audio');
      }
    }
  } catch (error) {
    errorLog('unsubscribeJoinedChannel failed', { channelId, error });
  }
  log('unsubscribeJoinedChannel: done', { channelId });
};

export const preloadAgoraChannel = async ({
  appId,
  channel,
  token,
  sessionId,
}) => {
  if (!loadAgoraScriptPromise) {
    await loadAgoraScriptPromise;
  }
  log('preload channel start', { appId, channel, token, sessionId });
  try {
    await window?.AgoraRTC?.preload(appId, channel, token, sessionId);
    log('successfully preloaded channel', { appId, channel, token, sessionId });
  } catch (error) {
    errorLog('preload channel failed', {
      error,
      appId,
      channel,
      token,
      sessionId,
    });
  }
};
